import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Crown, Zap, CalendarRange } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";

const Stat = ({ icon: I, label, value, testid }) => (
  <div className="panel p-5"><I size={16} className="text-[#9C4DDC] mb-3" strokeWidth={1.5} />
    <p className="font-display text-3xl" data-testid={testid}>{value ?? "—"}</p><p className="text-[11px] uppercase tracking-[0.15em] muted mt-1">{label}</p></div>
);

function UsersTable({ users }) {
  return (
    <div className="panel overflow-x-auto">
      <table className="w-full text-sm" data-testid="admin-users-table">
        <thead><tr className="text-left text-[10px] uppercase tracking-[0.15em] text-[#9C4DDC] border-b border-[#F0EAF7]">
          {["Email", "Plan", "Muses", "Cards (month)", "Signed up"].map((h) => <th key={h} className="p-4 font-medium whitespace-nowrap">{h}</th>)}</tr></thead>
        <tbody>{users.map((u) => (
          <tr key={u.id} className="border-b border-[#F0EAF7] last:border-0" data-testid={`admin-user-row-${u.id}`}>
            <td className="p-4">{u.email}{u.is_admin && <span className="ml-2 text-[10px] text-[#9C4DDC]">ADMIN</span>}</td>
            <td className="p-4 uppercase text-xs">{u.plan}</td><td className="p-4">{u.muses_count}</td><td className="p-4">{u.cards_this_month}</td>
            <td className="p-4 muted whitespace-nowrap">{u.created_at?.slice(0, 10)}</td>
          </tr>))}</tbody>
      </table>
    </div>
  );
}

function FormatEditor({ f, onSaved }) {
  const [d, setD] = useState(f);
  const save = async (patch = {}) => {
    try {
      const body = { name: d.name, description: d.description, structure_notes: d.structure_notes, default_length_seconds: d.default_length_seconds ? Number(d.default_length_seconds) : null, ...patch };
      const { data } = await api.put(`/admin/formats/${f.key}`, body);
      setD(data); onSaved(data); toast.success(`${data.name} saved`);
    } catch (err) { toast.error(formatErr(err)); }
  };
  const set = (k) => (e) => setD({ ...d, [k]: e.target.value });
  return (
    <div className="panel p-5 space-y-4" data-testid={`admin-format-${f.key}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs muted">{f.key}</p>
        <label className="flex items-center gap-2 text-xs muted">{d.enabled === false ? "Off" : "On"}
          <Switch checked={d.enabled !== false} onCheckedChange={(v) => save({ enabled: v })} data-testid={`admin-format-toggle-${f.key}`} /></label>
      </div>
      <div className="grid sm:grid-cols-[1fr_140px] gap-3">
        <div><label className="label">Name</label><input className="field" value={d.name} onChange={set("name")} data-testid={`admin-format-name-${f.key}`} /></div>
        <div><label className="label">Default length (s)</label><input type="number" className="field" value={d.default_length_seconds ?? ""} onChange={set("default_length_seconds")} data-testid={`admin-format-length-${f.key}`} /></div>
      </div>
      <div><label className="label">Description</label><input className="field" value={d.description} onChange={set("description")} data-testid={`admin-format-description-${f.key}`} /></div>
      <div><label className="label">Structure notes</label><textarea className="field min-h-[100px]" value={d.structure_notes} onChange={set("structure_notes")} data-testid={`admin-format-notes-${f.key}`} /></div>
      <button className="btn-primary py-2" onClick={() => save()} data-testid={`admin-format-save-${f.key}`}>Save format</button>
    </div>
  );
}

function RulesEditor() {
  const [v, setV] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/studio-rules").then(({ data }) => setV(data.value)); }, []);
  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/studio-rules", { value: v }); toast.success("Studio Rules saved — used from the next generation"); }
    catch (err) { toast.error(formatErr(err)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <p className="muted text-xs">This is the system prompt for every generation. The muse JSON and format JSON are appended automatically.</p>
      <textarea className="field min-h-[60vh] font-mono text-xs leading-relaxed" value={v} onChange={(e) => setV(e.target.value)} data-testid="admin-studio-rules-input" />
      <button className="btn-primary" onClick={save} disabled={busy} data-testid="admin-studio-rules-save-button">{busy ? "Saving…" : "Save Studio Rules"}</button>
    </div>
  );
}

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [formats, setFormats] = useState([]);
  const [denied, setDenied] = useState(false);
  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => setDenied(true));
    api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
    api.get("/admin/formats").then(({ data }) => setFormats(data)).catch(() => {});
  }, []);
  if (denied) return <div className="panel p-10 text-center" data-testid="admin-denied"><p className="font-display text-2xl">Admins only</p><p className="muted text-sm mt-2">You don't have access to this page.</p></div>;
  return (
    <div data-testid="admin-page">
      <p className="eyebrow mb-3">Admin</p>
      <h1 className="text-4xl sm:text-5xl mb-8">Studio control room</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
        <Stat icon={Users} label="Total users" value={stats?.total_users} testid="admin-stat-total-users" />
        <Stat icon={Crown} label="Pro users" value={stats?.pro_users} testid="admin-stat-pro-users" />
        <Stat icon={Zap} label="Cards today" value={stats?.cards_today} testid="admin-stat-cards-today" />
        <Stat icon={CalendarRange} label="Cards this week" value={stats?.cards_this_week} testid="admin-stat-cards-week" />
      </div>
      <Tabs defaultValue="users">
        <TabsList className="bg-[#FFFFFF] border border-[#F0EAF7] mb-6">
          <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="formats" data-testid="admin-tab-formats">Formats</TabsTrigger>
          <TabsTrigger value="rules" data-testid="admin-tab-rules">Studio Rules</TabsTrigger>
        </TabsList>
        <TabsContent value="users"><UsersTable users={users} /></TabsContent>
        <TabsContent value="formats"><div className="space-y-4">{formats.map((f) => <FormatEditor key={f.key} f={f} onSaved={(n) => setFormats((fs) => fs.map((x) => (x.key === n.key ? n : x)))} />)}</div></TabsContent>
        <TabsContent value="rules"><RulesEditor /></TabsContent>
      </Tabs>
    </div>
  );
}
